import { Alert, Button, Input, Popconfirm, Spin, Tooltip } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { MdDeleteOutline, MdEdit } from 'react-icons/md'

import { useNotification } from '@/Core/Hooks/useNotification'
import { SectionEmpty } from '@/Ship/Components/EntityCard'
import { classNames } from '@/Ship/Helpers/classNames'
import { formatDateTimeUk, UserChip } from '@/Ship/Helpers/fieldViews'

import styles from './CommentsFeed.module.css'
import { COMMENT_TEXT_MAX_LENGTH, CommentsFeedStore } from './CommentsFeedStore'

import type { FeedComment } from './CommentsFeedStore'

const { TextArea } = Input

export interface CommentsFeedProps {
    /** Колекція коментарів власника: `/services/client_comments` */
    restUrl: string
    /** Поле власника в тілі запиту й префільтрі: `client`, `contact`, `carrier` */
    ownerField: string
    /** Власник; без нього (форма створення) стрічки немає */
    ownerUuid: string | undefined
    /** `@id` власника — ним новий коментар прив'язується до власника */
    ownerIri: string | undefined
    /** `_permissions.comment` власника: показувати форму чи ні */
    canComment: boolean
}

/** Автор без даних (обліковий запис прибрано зі вибірки) — не порожнє місце. */
const UNKNOWN_AUTHOR = { title: 'Невідомий автор' }

/**
 * Стрічка коментарів праворуч у картці (`EntityBody aside`), ADR 027.
 *
 *  - **форма зверху** — лише з `_permissions.comment` власника; порожній текст
 *    не відправляється («Напишіть коментар.», як у легасі);
 *  - **новіші зверху**: автор, дата, текст як написали — з переносами рядків,
 *    без HTML;
 *  - **«Редагувати» й «Видалити»** — лише за `_permissions` самого коментаря:
 *    бек дозволяє їх автору або адміністратору, а коментар видаленого власника
 *    лишає тільки для читання. Ролей стрічка не питає;
 *  - **видалення незворотне**, тож лише через `Popconfirm`;
 *  - **обрізаний перелік називає, скільки приховав**: «Показано N із M» і
 *    «Показати ще».
 *
 * Розмітка власна, без antd `List`: той мемоїзує рядки, і стан редагування,
 * який читається лише всередині рядка, не доходив би до перемальовування.
 */
export const CommentsFeed = observer(({ restUrl, ownerField, ownerUuid, ownerIri, canComment }: CommentsFeedProps) => {
    const [store] = useState(() => new CommentsFeedStore(restUrl, ownerField))
    // Коментар, для якого відкрите підтвердження видалення: підказка іконки кошика
    // лягала поверх кнопки «Видалити» у Popconfirm і перекривала її
    const [confirmingUuid, setConfirmingUuid] = useState<string | null>(null)
    const { showError } = useNotification()

    useEffect(() => {
        void store.load(ownerUuid)
    }, [store, ownerUuid])

    const remove = (uuid: string): void => {
        void store.remove(uuid).then((error) => {
            if (error !== null) showError('Помилка', error)
        })
    }

    const renderComment = (comment: FeedComment) => {
        const isEditing = store.editingUuid === comment.uuid
        const canUpdate = comment._permissions?.update === true
        const canDelete = comment._permissions?.delete === true

        return (
            <li key={comment.uuid} className={classNames(styles['item'])} data-comment={comment.uuid}>
                <div className={classNames(styles['itemHeader'])}>
                    <span className={classNames(styles['author'])}>
                        <UserChip user={comment.createdBy ?? UNKNOWN_AUTHOR} />
                        <span className={classNames(styles['date'])}>{formatDateTimeUk(comment.createdAt)}</span>
                    </span>

                    {/* Дії — іконками з підказкою: стрічка вужча за блоки даних,
                        і дві підписані кнопки виштовхували б дату на другий рядок */}
                    {!isEditing && (canUpdate || canDelete) ? (
                        <span className={classNames(styles['actions'])}>
                            {canUpdate ? (
                                <Tooltip title="Редагувати">
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<MdEdit />}
                                        aria-label="Редагувати коментар"
                                        onClick={() => store.startEdit(comment)}
                                    />
                                </Tooltip>
                            ) : null}
                            {canDelete ? (
                                <Popconfirm
                                    title="Видалити коментар?"
                                    description="Коментар зникне зі стрічки назавжди."
                                    okText="Видалити"
                                    cancelText="Скасувати"
                                    okButtonProps={{ danger: true }}
                                    onConfirm={() => remove(comment.uuid)}
                                    onOpenChange={(open) => setConfirmingUuid(open ? comment.uuid : null)}
                                >
                                    <Tooltip
                                        title="Видалити"
                                        {...(confirmingUuid === comment.uuid ? { open: false } : {})}
                                    >
                                        <Button
                                            type="text"
                                            size="small"
                                            danger
                                            icon={<MdDeleteOutline />}
                                            aria-label="Видалити коментар"
                                            loading={store.removingUuid === comment.uuid}
                                        />
                                    </Tooltip>
                                </Popconfirm>
                            ) : null}
                        </span>
                    ) : null}
                </div>

                {isEditing ? (
                    <div className={classNames(styles['edit'])}>
                        <TextArea
                            value={store.editingText}
                            onChange={(event) => store.setEditingText(event.target.value)}
                            autoSize={{ minRows: 3, maxRows: 12 }}
                            maxLength={COMMENT_TEXT_MAX_LENGTH}
                            aria-label="Текст коментаря"
                            status={store.editingError ? 'error' : ''}
                        />
                        {store.editingError ? (
                            <div className={classNames(styles['error'])}>{store.editingError}</div>
                        ) : null}
                        <div className={classNames(styles['formActions'])}>
                            <Button size="small" onClick={() => store.cancelEdit()}>
                                Скасувати
                            </Button>
                            <Button
                                type="primary"
                                size="small"
                                loading={store.isSavingEdit}
                                onClick={() => void store.saveEdit()}
                            >
                                Зберегти
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className={classNames(styles['text'])}>{comment.text ?? ''}</div>
                )}
            </li>
        )
    }

    const hidden = store.total - store.items.length

    return (
        <div className={classNames(styles['feed'])}>
            {canComment && ownerIri ? (
                <div className={classNames(styles['form'])}>
                    <TextArea
                        value={store.draft}
                        onChange={(event) => store.setDraft(event.target.value)}
                        autoSize={{ minRows: 3, maxRows: 12 }}
                        maxLength={COMMENT_TEXT_MAX_LENGTH}
                        placeholder="Ваш коментар"
                        aria-label="Новий коментар"
                        status={store.draftError ? 'error' : ''}
                    />
                    {store.draftError ? <div className={classNames(styles['error'])}>{store.draftError}</div> : null}
                    <div className={classNames(styles['formActions'])}>
                        <Button type="primary" loading={store.isSubmitting} onClick={() => void store.submit(ownerIri)}>
                            Відправити
                        </Button>
                    </div>
                </div>
            ) : null}

            {store.failed ? <Alert type="error" showIcon title="Не вдалося завантажити коментарі." /> : null}

            {store.isLoading ? (
                <Spin style={{ display: 'block', margin: '24px auto' }} />
            ) : store.items.length === 0 && !store.failed ? (
                <SectionEmpty>Коментарів ще немає.</SectionEmpty>
            ) : (
                <ul className={classNames(styles['list'])}>{store.items.map(renderComment)}</ul>
            )}

            {hidden > 0 && !store.isLoading ? (
                <div className={classNames(styles['more'])}>
                    <span>
                        Показано {store.items.length} із {store.total}
                    </span>
                    <Button size="small" loading={store.isLoadingMore} onClick={() => void store.loadMore()}>
                        Показати ще
                    </Button>
                </div>
            ) : null}
        </div>
    )
})
